import { useEffect, useState, useMemo } from "react";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";

interface UserRight {
  id: number;
  group_name: string;
  module: string;
  can_view: number;
  can_create: number;
  can_edit: number;
  can_delete: number;
  created_at: string;
}

export default function UserRights() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<UserRight[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  /* =========================
     SYSTEM MODULES
  ========================= */

  const SYSTEM_MODULES = [
    { label: "Dashboard", value: "dashboard" },
    { label: "Market Price Analysis", value: "market_price_analysis" },
    { label: "Products", value: "products" },
    { label: "Categories", value: "categories" },
    { label: "Subcategories", value: "subcategories" },
    { label: "Product Types", value: "types" },
    { label: "Brands", value: "brands" },
    { label: "Orders", value: "orders" },
    { label: "Payments", value: "payments" },
    { label: "Subscribers", value: "subscribers" },
    { label: "Demo Bookings", value: "demo_bookings" },
    { label: "Admin Users", value: "users" },
    { label: "User Groups", value: "user_groups" },
    { label: "User Rights", value: "user_rights" },
    { label: "Market Analysis Plans", value: "market_analysis_plans" },
    { label: "Promotion Banners", value: "banners" },
    { label: "Algorithm Setup", value: "algorithm" },
    { label: "Services", value: "services" },
    { label: "Site Search", value: "site_search" },
    { label: "Support Tickets", value: "support_tickets" },
    { label: "Reviews", value: "reviews" },
    { label: "Regions", value: "regions" },
    { label: "Vendors", value: "vendors" },
    { label: "Email Bodies", value: "email_bodies" },
    { label: "Email Templates", value: "email_templates" },
    { label: "Payment Gateway", value: "payment_gateway" },
    { label: "Email Variables", value: "email_variables" },
    { label: "SMTP", value: "smtp" },
    { label: "SMTP Settings", value: "smtp_settings" },
    { label: "Public Emails", value: "public_emails" },
    { label: "Send Campaign", value: "send_campaign" },
    { label: "Polices", value: "policies" },
    { label: "Footer", value: "footer" },
    { label: "About", value: "about" },
    { label: "Latest News", value: "latest_news" },
    { label: "Software", value: "software" },
    { label: "Software Modules", value: "software-modules" },
    { label: "Software Module Details", value: "software-module-details" },
    { label: "Career Jobs", value: "career_jobs" },
    { label: "Career Applications", value: "career_applications" },
    { label: "Team Members", value: "team_members" },
    { label: "Promotions", value: "promotions" },
    { label: "Discount Vouchers", value: "discount_vouchers" },
    { label: "Testimonials", value: "testimonials" },
  ];

  /* =========================
  FETCH RIGHTS
  ========================= */

  const fetchRights = async () => {
    const res = await fetch(`${BASE}/admin/users/rights`, {
      credentials: "include",
    });

    const result = await res.json();
    setData(result.data);
  };

  /* =========================
     FETCH GROUPS
  ========================= */

  const fetchGroups = async () => {
    const res = await fetch(`${BASE}/admin/users/groups`, {
      credentials: "include",
    });

    const result = await res.json();
    setGroups(result.data);
  };

  useEffect(() => {
    fetchRights();
    fetchGroups();
  }, []);

  /* =========================
     COLUMNS
  ========================= */

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "group_name", label: "User Group" },
    {
      key: "module",
      label: "Module",
      render: (row: UserRight) => {
        const formattedModule = row.module
          ?.split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        return <span>{formattedModule}</span>;
      },
    },

    {
      key: "can_view",
      label: "View",
      render: (row: UserRight) => (
        <span className={row.can_view ? "badge-success" : "badge-danger"}>
          {row.can_view ? "Yes" : "No"}
        </span>
      ),
    },

    {
      key: "can_create",
      label: "Create",
      render: (row: UserRight) => (
        <span className={row.can_create ? "badge-success" : "badge-danger"}>
          {row.can_create ? "Yes" : "No"}
        </span>
      ),
    },

    {
      key: "can_edit",
      label: "Edit",
      render: (row: UserRight) => (
        <span className={row.can_edit ? "badge-success" : "badge-danger"}>
          {row.can_edit ? "Yes" : "No"}
        </span>
      ),
    },

    {
      key: "can_delete",
      label: "Delete",
      render: (row: UserRight) => (
        <span className={row.can_delete ? "badge-success" : "badge-danger"}>
          {row.can_delete ? "Yes" : "No"}
        </span>
      ),
    },

    {
      key: "created_at",
      label: "Created",
      render: (row: UserRight) => formatDateToDMY(row.created_at),
    },
  ];

  /* =========================
     FORM FIELDS
  ========================= */

  const rightsFields: FormField[] = useMemo(
    () => [
      {
        key: "group_id",
        label: "User Group",
        type: "select",
        options: groups.map((g) => ({
          label: g.name,
          value: g.id,
        })),
        colSpan: 2,
      },

      {
        key: "rights",
        label: "Module Permissions",
        type: "permission-matrix",
        options: SYSTEM_MODULES,
        colSpan: 2,
      },
    ],
    [groups],
  );

  /* =========================
     HANDLE SUBMIT
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/users/rights`
          : `${BASE}/admin/users/rights/update`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (result.success || res.ok) {
        fetchRights();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "User rights added successfully."
              : "User rights updated successfully.",
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  /* =========================
     DELETE
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete User Rights",
      message: "Are you sure you want to delete this permission?",
      confirmText: "Delete",
      onConfirm: async () => {
        await fetch(`${BASE}/admin/users/rights/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });

        fetchRights();
      },
    });
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div>
      <AdminListToolkit
        title="User Rights"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={async (row) => {
          const res = await fetch(
            `${BASE}/admin/users/rights/group/${row.group_id}`,
            { credentials: "include" },
          );

          const result = await res.json();

          setModalMode("edit");

          setActiveRow({
            ...row,
            modules: result.data,
          });

          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport
        exportFileName="user-rights"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="User Rights"
        fields={rightsFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        size="lg"
      />
    </div>
  );
}
